import { Injectable, inject } from "@angular/core";
import { map, Observable, EMPTY, from, of } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { catchError } from "rxjs";
import { ApiService } from "src/app/shared/services/api.service";
import { MapActions } from "src/app/ngrx/actions";
import { DisplayPropertyKeys, AssetType } from "src/app/shared/types/enums/map.enums";
import { IAsset, ILocationGroupAsset } from "src/app/shared/types/interfaces/asset.interface";
import { ApiResponse } from "src/app/core/models/api-response.model";
import { IAssetSpec } from "src/app/shared/types/interfaces/map.interface";
import { IndexedDBNewFuseService } from "src/app/shared/services/indexed-db/indexed-db-newfuse.service";
import { NewLoadFuseData, NewLoadFuseRequest } from "../interfaces/newLoadFuse";
import { MapActionTypes } from "src/app/ngrx/actions/types";

@Injectable({
  providedIn: "root",
})

/**
 * Provides functions requesting asset details and asset spec data via bacck-end API
 */
export class AssetDetailsService extends ApiService {
  private indexedDBNewFuseService = inject(IndexedDBNewFuseService);

  public render: boolean = false;

  constructor() {
    super();
  }

  /**
   * Retrieves asset details for a list of asset IDs
   * @param assetIds -- small world asset IDs
   * @param proxy -- asset type
   * @param is_geojson -- type of response required
   * @returns response -- a list of asset details
   */
  private requestAssetDetailsInternal(
    assetIds: string[],
    proxy: string,
    assetTypes?: string[],
    is_geojson?: boolean
  ): Observable<any> {
    const queryStringParams = this.makeQueryStringParams();
    const url = `${this.api}/asset/details/${proxy}${queryStringParams}`;
    let body = "";
    body = assetTypes
      ? JSON.stringify({
          ...this.makeRequestBodyExtensionParams(),
          asset_ids: assetIds,
          asset_types: assetTypes,
          is_geojson: is_geojson,
        })
      : JSON.stringify({
          ...this.makeRequestBodyExtensionParams(),
          asset_ids: assetIds,
          is_geojson: is_geojson,
        });
    const response: any = this.httpClient.post(url, body);
    return response;
  }

  /**
   * Retrieves asset details suitable for leftside panel.
   * This is the full set of information for an asset.
   * @param assetIds -- small world asset IDs
   * @param proxy -- asset type
   * @returns response -- a list of asset details
   */
  public requestAssetDetails(assetIds: string[], proxy: string, assetTypes?: string[]): Observable<any> {
    return this.requestAssetDetailsInternal(assetIds, proxy, assetTypes, false);
  }

  /**
   * Retrieves assets as a geojson feature collection based on an asset id (or list of ids).
   * Equivalent to what the '/tile' endpoint returns.
   * Returns fewer fields as compared to requestAssetDetails function.
   * @param assetIds -- small world asset IDs
   * @param proxy -- asset type
   * @returns response -- a list of asset details
   */
  public requestAssetFeatures(assetIds: string[], proxy: string, assetTypes?: string[]): Observable<any> {
    return this.requestAssetDetailsInternal(assetIds, proxy, assetTypes, true);
  }

  /**
   * Fetches multiple specs at once for an asset detail object
   * This is used by the store to proactively load all specs when an asset is loaded
   * @param specIds - Array of spec IDs to fetch
   * @returns Observable that emits the raw spec data from the API
   */
  public fetchAllSpecs(specIds: string[]): Observable<IAssetSpec[]> {
    if (!specIds || specIds.length === 0) {
      return EMPTY;
    }

    const url = `${this.api}/asset/specs`;
    const body = {
      spec_ids: specIds,
    };

    return this.httpClient.post<ApiResponse<IAssetSpec[]>>(url, body).pipe(map(response => response.output.data));
  }

  public getAssetDetails(asset: IAsset | ILocationGroupAsset) {
    if (asset && asset[DisplayPropertyKeys.AssetType] == AssetType.LOCATION_GROUP) {
      this.getAssetDetailsGroup(asset as ILocationGroupAsset);
    } else {
      this.getAssetDetailsSingle(asset);
    }
  }

  public getNewFuseData(payload: NewLoadFuseRequest) {
    const key = this.buildNewFuseKeyFromPayload(payload);
    const url = `${this.api}/new-fuse`;
    return from(this.indexedDBNewFuseService.getNewFuseData(key)).pipe(
      switchMap(cachedData => {
        if (cachedData) {
          return of(cachedData);
        } else {
          return this.httpClient.post<ApiResponse<NewLoadFuseData>>(url, payload).pipe(
            map(response => response.output.data),
            tap(apiData => {
              if (apiData) {
                apiData.input = payload;
                this.indexedDBNewFuseService.saveNewFuseData(key, apiData);
              }
            }),
            catchError(error => {
              this.store.dispatch({
                type: MapActionTypes.ServiceError,
                payload: error,
              });

              throw error;
            })
          );
        }
      })
    );
  }

  /**
   * Concatenates and trims all relevant payload components into a single string key.
   * @param payload - The payload object passed to getNewFuseData
   * @returns The concatenated string key
   */
  private buildNewFuseKeyFromPayload(payload: any): string {
    if (!payload) return "";
    return ["asset_id", "tr_type", "kva", "phasing", "circuit_id"]
      .map(key => payload[key]?.toString().trim() ?? "")
      .join("-");
  }

  private getAssetDetailsGroup(asset: ILocationGroupAsset) {
    let assetIds = [];
    let assetTypes = [];
    for (let a of asset.assets) {
      assetIds.push(a.asset_id);
      assetTypes.push(a.asset_type?.toLowerCase());
    }
    this.render = false;
    this.store.dispatch(
      MapActions.getAssetDetails({
        payload: {
          assetIds: assetIds,
          assetType: asset[DisplayPropertyKeys.AssetType],
          assetTypes: assetTypes,
          asset: asset,
        },
      })
    );
  }

  private getAssetDetailsSingle(asset: IAsset) {
    if (asset?.[DisplayPropertyKeys.AssetId]) {
      this.store.dispatch(
        MapActions.getAssetDetails({
          payload: {
            assetIds: [asset[DisplayPropertyKeys.AssetId]],
            assetType: asset[DisplayPropertyKeys.AssetType],
          },
        })
      );
    }
  }
}
